import { ChevronDown } from "lucide-react";

import type { Locale } from "@/lib/i18n";

export type FaqBlockLink = { label: string; href: string };
export type FaqBlockItem = { q: string; a: string; links?: FaqBlockLink[] };

const HEADING: Record<Locale, string> = {
  sv: "Vanliga frågor",
  en: "Frequently asked questions",
  es: "Preguntas frecuentes",
};

/** Kompakt FAQ-block med FAQPage-schema, för de tyngre undersidorna. */
export function FaqBlock({
  items,
  locale = "sv",
  title,
}: {
  items: FaqBlockItem[];
  locale?: Locale;
  title?: string;
}) {
  if (items.length === 0) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-5 pb-12 sm:pb-16">
      <h2 className="text-2xl font-semibold">{title ?? HEADING[locale]}</h2>
      <div className="mt-5 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card shadow-soft">
        {items.map((item, index) => (
            <details key={item.q} open={index === 0} className="group">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-sand/60 [&::-webkit-details-marker]:hidden">
                  <span className="font-display text-base font-semibold">{item.q}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180 group-open:text-primary"
                  />
              </summary>
                <div className="px-5 pb-5">
                  <p className="text-[0.95rem] leading-relaxed text-foreground/90">{item.a}</p>
                  {item.links?.length ? (
                    <p className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                      {item.links.map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          target={link.href.startsWith("http") ? "_blank" : undefined}
                          rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="link-quiet font-medium text-primary"
                        >
                          {link.label}
                        </a>
                      ))}
                    </p>
                  ) : null}
                </div>
            </details>
        ))}
      </div>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </section>
  );
}
